import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActualServer, Account } from '../types';
import { useAccounts } from '../hooks/useAccounts';
import { AccountConfiguration, useAccountConfiguration } from '../hooks/useAccountConfiguration';
import { getCountryFlag } from '../utils/countries';

interface AccountsDisplayProps {
  activeServer: ActualServer | null;
  onAccountSelect?: (account: Account, config: AccountConfiguration) => void;
  onAccountDeselect?: () => void;
  accountConfig: ReturnType<typeof useAccountConfiguration>;
  onShowSettings?: (account: Account) => void;
  onLinkAccount?: (account: Account) => void;
}

const AccountsDisplay: React.FC<AccountsDisplayProps> = ({ 
  activeServer, 
  onAccountSelect, 
  onAccountDeselect,
  accountConfig,
  onShowSettings,
  onLinkAccount
}) => {
  const { t } = useTranslation();
  const [showUnlinkedAccounts, setShowUnlinkedAccounts] = React.useState(false);

  
  const { accounts, isLoading, error, isRefetching } = useAccounts({
    server: activeServer,
    budgetId: activeServer?.defaultBudgetId,
    budgetPassword: activeServer?.defaultBudgetPassword,
    enabled: !!(activeServer && activeServer.defaultBudgetId)
  });

  // Separate linked and unlinked accounts
  const linkedAccounts = accounts.filter(account => {
    const config = accountConfig.getAccountConfiguration(account);
    // Only consider linked if both country and bank are configured
    return config.selectedCountry && config.selectedBank;
  });

  const unlinkedAccounts = accounts.filter(account => {
    const config = accountConfig.getAccountConfiguration(account);
    // Consider unlinked if either country or bank is missing
    return !(config.selectedCountry && config.selectedBank);
  });

  const hasLinkedAccounts = linkedAccounts.length > 0;

  const handleAccountClick = (account: Account) => {
    // Select the account and get its cached configuration
    // Use the budget currency as default if available
    const config = accountConfig.selectAccount(account, activeServer?.defaultBudgetCurrency);
    
    // Notify parent component about the selection
    if (onAccountSelect) {
      onAccountSelect(account, config);
    }
  };

  const handleSettingsClick = (e: React.MouseEvent, account: Account) => {
    e.stopPropagation(); // Prevent account selection
    if (onShowSettings) {
      onShowSettings(account);
    }
  };

  const handleLinkAccount = (account: Account) => {
    if (onLinkAccount) {
      onLinkAccount(account);
    }
  };

  // Don't show anything if no active server or no default budget
  if (!activeServer || !activeServer.defaultBudgetId) {
    return null;
  }

  return (
    <div className="accounts-display">
      <div className="accounts-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <label className="selection-label">
            {hasLinkedAccounts ? t('linkedAccounts', { ns: 'tools' }) : t('linkYourFirstAccount', { ns: 'tools' })}
          </label>
          {accountConfig.selectedAccountId && onAccountDeselect && (
            <button
              type="button"
              onClick={onAccountDeselect}
              style={{
                background: 'none',
                border: '1px solid #dc3545',
                color: '#dc3545',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dc3545';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#dc3545';
              }}
            >
              {t('clearSelection', { ns: 'tools' })}
            </button>
          )}
        </div>

        {!accountConfig.selectedAccountId && hasLinkedAccounts && (
          <p className="account-selection-hint">
            {t('selectLinkedAccountHint', { ns: 'tools' })}
          </p>
        )}
        
        {isLoading ? (
          <div className="accounts-loading">
            <div className="spinner" style={{ 
              display: 'inline-block', 
              width: '16px', 
              height: '16px', 
              border: '2px solid #ddd', 
              borderTop: '2px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '8px'
            }}></div>
            <span>{t('loadingAccounts', { ns: 'import' })}</span>
          </div>
        ) : error ? (
          <div className="accounts-error">
            <small style={{ color: '#dc3545' }}>{error}</small>
          </div>
        ) : accounts.length > 0 ? (
          <div className="accounts-grid">
            {isRefetching && (
              <div className="refresh-indicator" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                fontSize: '0.8rem',
                color: '#666',
                marginBottom: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div className="spinner" style={{ 
                  display: 'inline-block', 
                  width: '12px', 
                  height: '12px', 
                  border: '1px solid #ddd', 
                  borderTop: '1px solid #007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '6px'
                }}></div>
                <span>{t('refreshingAccounts', { ns: 'import' })}</span>
              </div>
            )}
            
            {/* Show linked accounts */}
            {hasLinkedAccounts && (
                          <div className="accounts-cards-container" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              padding: '0',
              marginBottom: '1rem'
            }}>
                {linkedAccounts.map(account => {
                  const isSelected = accountConfig.selectedAccountId === account.id;
                  const accountConfigData = accountConfig.getAccountConfiguration(account);
                                  const hasConfiguration = accountConfigData && (
                  accountConfigData.selectedCountry && 
                  accountConfigData.selectedBank
                );
                  
                  return (
                                      <div 
                    key={account.id} 
                    className={`account-card ${isSelected ? 'selected' : ''} ${hasConfiguration ? 'has-config' : ''}`}
                    onClick={() => handleAccountClick(account)}
                    style={{
                      backgroundColor: '#ffffff',
                      border: isSelected ? '2px solid #007bff' : '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '0.75rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      position: 'relative',
                      width: '200px',
                      minHeight: '100px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      flexShrink: 0
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      {/* Settings icon for configured accounts */}
                      {hasConfiguration && (
                        <button
                          onClick={(e) => handleSettingsClick(e, account)}
                          style={{
                            position: 'absolute',
                            top: '0.5rem',
                            [document.dir === 'rtl' ? 'left' : 'right']: '0.5rem',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            borderRadius: '4px',
                            color: '#6c757d',
                            fontSize: '1rem'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.color = '#007bff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#6c757d';
                          }}
                          title={t('accountSettings', { ns: 'tools' })}
                        >
                          ⚙️
                        </button>
                      )}
                      
                      <div className="account-main-info">
                        <h4 style={{
                          margin: '0 0 0.25rem 0',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#212529',
                          lineHeight: '1.2',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {account.name}
                        </h4>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          marginBottom: '0.25rem'
                        }}>
                          <span style={{
                            fontSize: '0.65rem',
                            color: '#6c757d',
                            backgroundColor: '#f8f9fa',
                            padding: '0.15rem 0.4rem',
                            borderRadius: '8px',
                            fontWeight: '500'
                          }}>
                            {account.type}
                          </span>
                          {account.closed && (
                            <span style={{
                              fontSize: '0.65rem',
                              color: '#dc3545',
                              backgroundColor: '#f8d7da',
                              padding: '0.15rem 0.4rem',
                              borderRadius: '8px',
                              fontWeight: '500'
                            }}>
                              {t('closed', { ns: 'import' })}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {hasConfiguration && (
                        <div className="account-config-summary" style={{
                          marginTop: 'auto',
                          paddingTop: '0.5rem',
                          borderTop: '1px solid #f1f3f4'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            {accountConfigData.selectedCountry && (
                              <span style={{ fontSize: '1rem' }}>
                                {getCountryFlag(accountConfigData.selectedCountry)}
                              </span>
                            )}
                            {accountConfigData.selectedBankName && (
                              <span style={{
                                fontSize: '0.7rem',
                                color: '#495057',
                                fontWeight: '500',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }} title={accountConfigData.selectedBankName}>
                                {accountConfigData.selectedBankName}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {!hasConfiguration && (
                        <div style={{
                          marginTop: 'auto',
                          paddingTop: '0.5rem',
                          textAlign: 'center'
                        }}>
                          <small style={{
                            color: '#6c757d',
                            fontSize: '0.65rem',
                            fontStyle: 'italic'
                          }}>
                            {t('noConfiguration', { ns: 'tools' })}
                          </small>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Show all accounts when no linked accounts exist */}
            {!hasLinkedAccounts && (
              <div className="accounts-cards-container" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '1rem',
                padding: '0'
              }}>
                {accounts.map(account => {
                  const isSelected = accountConfig.selectedAccountId === account.id;
                  
                  return (
                    <div 
                      key={account.id} 
                      className={`account-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleLinkAccount(account)}
                      style={{
                        backgroundColor: '#ffffff',
                        border: isSelected ? '2px solid #007bff' : '1px solid #e9ecef',
                        borderRadius: '12px',
                        padding: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                      }}
                    >
                      <div className="account-main-info">
                        <h4 style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: '#212529',
                          lineHeight: '1.2'
                        }}>
                          {account.name}
                        </h4>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.5rem'
                        }}>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#6c757d',
                            backgroundColor: '#f8f9fa',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontWeight: '500'
                          }}>
                            {account.type}
                          </span>
                          {account.closed && (
                            <span style={{
                              fontSize: '0.75rem',
                              color: '#dc3545',
                              backgroundColor: '#f8d7da',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px',
                              fontWeight: '500'
                            }}>
                              {t('closed', { ns: 'import' })}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div style={{
                        marginTop: 'auto',
                        paddingTop: '0.75rem',
                        textAlign: 'center'
                      }}>
                        <small style={{
                          color: '#007bff',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {t('clickToLink', { ns: 'tools' })}
                        </small>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Show unlinked accounts section */}
            {hasLinkedAccounts && unlinkedAccounts.length > 0 && (
              <div>
                {!showUnlinkedAccounts ? (
                  <button
                    onClick={() => setShowUnlinkedAccounts(true)}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      border: '2px dashed #dee2e6',
                      backgroundColor: 'transparent',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      color: '#6c757d',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#007bff';
                      e.currentTarget.style.color = '#007bff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#dee2e6';
                      e.currentTarget.style.color = '#6c757d';
                    }}
                  >
                    {t('linkAnotherAccount', { ns: 'tools' })} ({unlinkedAccounts.length})
                  </button>
                ) : (
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', color: '#495057' }}>
                        {t('unlinkedAccounts', { ns: 'tools' })} ({unlinkedAccounts.length})
                      </h4>
                      <button
                        onClick={() => setShowUnlinkedAccounts(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6c757d',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('hideUnlinked', { ns: 'tools' })}
                      </button>
                    </div>
                    <div className="accounts-cards-container" style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                      padding: '0'
                    }}>
                      {unlinkedAccounts.map(account => {
                        const isSelected = accountConfig.selectedAccountId === account.id;
                        
                        return (
                          <div 
                            key={account.id} 
                            className={`account-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleLinkAccount(account)}
                            style={{
                              backgroundColor: '#ffffff',
                              border: isSelected ? '2px solid #007bff' : '1px solid #e9ecef',
                              borderRadius: '12px',
                              padding: '0.75rem',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              position: 'relative',
                              width: '200px',
                              minHeight: '100px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                            }}
                          >
                            <div className="account-main-info">
                              <h4 style={{
                                margin: '0 0 0.25rem 0',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#212529',
                                lineHeight: '1.2',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {account.name}
                              </h4>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                marginBottom: '0.25rem'
                              }}>
                                <span style={{
                                  fontSize: '0.65rem',
                                  color: '#6c757d',
                                  backgroundColor: '#f8f9fa',
                                  padding: '0.15rem 0.4rem',
                                  borderRadius: '8px',
                                  fontWeight: '500'
                                }}>
                                  {account.type}
                                </span>
                                {account.closed && (
                                  <span style={{
                                    fontSize: '0.65rem',
                                    color: '#dc3545',
                                    backgroundColor: '#f8d7da',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '8px',
                                    fontWeight: '500'
                                  }}>
                                    {t('closed', { ns: 'import' })}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div style={{
                              marginTop: 'auto',
                              paddingTop: '0.5rem',
                              textAlign: 'center'
                            }}>
                              <small style={{
                                color: '#007bff',
                                fontSize: '0.65rem',
                                fontWeight: '500'
                              }}>
                                {t('clickToLink', { ns: 'tools' })}
                              </small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="accounts-empty">
            <small>{t('noAccountsFound', { ns: 'tools' })}</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountsDisplay; 